import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Users, UserPlus, Shield, ShieldCheck, Copy, Check, 
  Building2, Mail, Calendar, Clock, AlertTriangle, ExternalLink, CheckCircle, Trash2, UserMinus,
  FileCheck, Loader2, Key, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import MFASettings from '../components/MFASettings';

export default function OrganizationSettingsPage() {
  const { user, viewingOrg, refreshEnabledFrameworks } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  
  const [tenantInfo, setTenantInfo] = useState(null);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showInviteSuccessDialog, setShowInviteSuccessDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [removingUser, setRemovingUser] = useState(false);
  const [newInviteData, setNewInviteData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('client');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Framework management state
  const [frameworks, setFrameworks] = useState([]);
  const [loadingFrameworks, setLoadingFrameworks] = useState(true);
  const [savingFrameworks, setSavingFrameworks] = useState(false);
  
  // MFA Policy state
  const [mfaPolicy, setMfaPolicy] = useState(null);
  const [loadingMfaPolicy, setLoadingMfaPolicy] = useState(true);
  const [savingMfaPolicy, setSavingMfaPolicy] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
      fetchFrameworks();
      fetchMfaPolicy();
    }
  }, [isAdmin, viewingOrg]);

  const fetchFrameworks = async () => {
    setLoadingFrameworks(true);
    try {
      let endpoint = `${API}/tenant/frameworks`;
      // For super admin viewing a specific org, use admin endpoint
      if (isSuperAdmin && viewingOrg?.id) {
        endpoint = `${API}/admin/tenants/${viewingOrg.id}/frameworks`;
      }
      const response = await axios.get(endpoint);
      setFrameworks(response.data.frameworks || []);
    } catch (error) {
      console.error('Failed to fetch frameworks:', error);
      if (!(isSuperAdmin && !viewingOrg)) {
        toast.error('Failed to load framework settings');
      }
    } finally {
      setLoadingFrameworks(false);
    }
  };

  const fetchMfaPolicy = async () => {
    setLoadingMfaPolicy(true);
    try {
      let endpoint = `${API}/tenant/mfa-policy`;
      if (isSuperAdmin && viewingOrg?.id) {
        endpoint = `${API}/tenant/mfa-policy?view_tenant_id=${viewingOrg.id}`;
      }
      const response = await axios.get(endpoint);
      setMfaPolicy(response.data);
    } catch (error) {
      console.error('Failed to fetch MFA policy:', error);
    } finally {
      setLoadingMfaPolicy(false);
    }
  };

  const handleMfaPolicyChange = async (field, value) => {
    setSavingMfaPolicy(true);
    try {
      await axios.put(`${API}/tenant/mfa-policy?${field}=${value}`);
      setMfaPolicy(prev => ({ ...prev, [field]: value }));
      toast.success('MFA policy updated');
    } catch (error) {
      console.error('Failed to update MFA policy:', error);
      toast.error('Failed to update MFA policy');
    } finally {
      setSavingMfaPolicy(false);
    }
  };

  const handleToggleFramework = async (frameworkId, currentEnabled) => {
    // Optimistic update
    setFrameworks(prev => prev.map(fw => 
      fw.id === frameworkId ? { ...fw, enabled: !currentEnabled } : fw
    ));
    
    try {
      // For super admin viewing a specific org, use admin endpoint
      if (isSuperAdmin && viewingOrg?.id) {
        const enabledFrameworks = frameworks
          .map(fw => fw.id === frameworkId ? { ...fw, enabled: !currentEnabled } : fw)
          .filter(fw => fw.enabled)
          .map(fw => fw.id);
        
        await axios.put(`${API}/admin/tenants/${viewingOrg.id}/frameworks`, {
          enabled_frameworks: enabledFrameworks
        });
        toast.success(`Framework ${currentEnabled ? 'disabled' : 'enabled'}`);
        refreshEnabledFrameworks();
        return;
      }
      
      const endpoint = currentEnabled 
        ? `${API}/tenant/frameworks/${frameworkId}/disable`
        : `${API}/tenant/frameworks/${frameworkId}/enable`;
      
      await axios.post(endpoint);
      toast.success(`Framework ${currentEnabled ? 'disabled' : 'enabled'}`);
      
      // Refresh the enabled frameworks in context so navigation updates
      if (refreshEnabledFrameworks) {
        refreshEnabledFrameworks();
      }
    } catch (error) {
      // Revert on error
      setFrameworks(prev => prev.map(fw => 
        fw.id === frameworkId ? { ...fw, enabled: currentEnabled } : fw
      ));
      toast.error(error.response?.data?.detail || 'Failed to update framework');
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // For super admin viewing a specific org, use view_tenant_id param
      const viewParam = (isSuperAdmin && viewingOrg?.id) ? `?view_tenant_id=${viewingOrg.id}` : '';
      
      const [tenantRes, usersRes, invitesRes] = await Promise.all([
        axios.get(`${API}/tenant/info${viewParam}`),
        axios.get(`${API}/tenant/users${viewParam}`),
        axios.get(`${API}/tenant/invites${viewParam}`)
      ]);
      setTenantInfo(tenantRes.data);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      console.error('Failed to fetch organization data:', error);
      // For super admin without viewing org, show a message instead of error
      if (isSuperAdmin && !viewingOrg) {
        setTenantInfo(null);
        setUsers([]);
        setInvites([]);
      } else {
        toast.error('Failed to load organization data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setCreatingInvite(true);
    try {
      const response = await axios.post(`${API}/tenant/invite`, {
        email: inviteEmail,
        role: inviteRole
      });
      // Show success dialog with instructions
      setNewInviteData({
        email: inviteEmail,
        role: inviteRole,
        invite_code: response.data.invite_code
      });
      setShowInviteDialog(false);
      setShowInviteSuccessDialog(true);
      setInviteEmail('');
      setInviteRole('client');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setCreatingInvite(false);
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Invite code copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const copyInviteInstructions = () => {
    if (!newInviteData) return;
    const appUrl = window.location.origin;
    const instructions = `You've been invited to join ${tenantInfo?.name || 'our organization'} on Cyber Forte Compliance Manager!

To get started:
1. Go to: ${appUrl}
2. Click "Have an invite code? Join existing org"
3. Enter your invite code: ${newInviteData.invite_code}
4. Create your account with email: ${newInviteData.email}
5. Set your password and you're in!

Your role: ${newInviteData.role === 'admin' ? 'Admin (Full Access)' : 'Client (View & Comment)'}

This invite code expires in 7 days.`;
    
    navigator.clipboard.writeText(instructions);
    toast.success('Invite instructions copied to clipboard!');
  };

  const handleDeleteInvite = async (inviteId) => {
    if (!window.confirm('Delete this invite?')) return;
    try {
      await axios.delete(`${API}/tenant/invites/${inviteId}`);
      toast.success('Invite deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invite');
    }
  };

  const handleRegenerateInvite = async (invite) => {
    try {
      const res = await axios.post(`${API}/tenant/invites/${invite.id}/regenerate`);
      toast.success(`New invite code generated for ${res.data.email}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate invite');
    }
  };

  const handleRemoveUser = async () => {
    if (!userToRemove) return;
    
    setRemovingUser(true);
    try {
      await axios.delete(`${API}/tenant/users/${userToRemove.id}`);
      toast.success(`${userToRemove.email} has been removed from the organization`);
      setShowRemoveDialog(false);
      setUserToRemove(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    } finally {
      setRemovingUser(false);
    }
  };

  const openRemoveDialog = (member) => {
    setUserToRemove(member);
    setShowRemoveDialog(true);
  };

  const getRoleBadgeClass = (role) => {
    return role === 'admin' 
      ? 'bg-purple-100 text-purple-800' 
      : 'bg-blue-100 text-blue-800';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">Access Restricted</h2>
          <p className="text-yellow-700">
            Only administrators can access organization settings. 
            Contact your admin if you need access.
          </p>
        </div>
      </div>
    );
  }

  // Super admin without viewing org should select one first
  if (isSuperAdmin && !viewingOrg) {
    return (
      <div className="p-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <Building2 className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-blue-800 mb-2">Select an Organization</h2>
          <p className="text-blue-700">
            Please select an organization from the Organizations Overview to manage its settings.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-lg text-gray-600">Loading organization settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-slate-600" />
            <h1 className="text-3xl font-heading font-bold">Organization Settings</h1>
          </div>
          <p className="text-gray-600 mt-2">
            Manage your organization, team members, and invitations
          </p>
        </div>
      </div>

      {/* Organization Info Card */}
      {tenantInfo && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-600" />
            Organization Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Organization Name</p>
              <p className="font-medium">{tenantInfo.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Subscription Plan</p>
              <p className="font-medium capitalize">{tenantInfo.subscription_plan}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Users</p>
              <p className="font-medium">{tenantInfo.user_count}</p>
            </div>
          </div>

          {/* Logo upload */}
          {isAdmin && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <p className="text-sm font-medium text-slate-700 mb-1">Organization Logo</p>
              <p className="text-xs text-slate-500 mb-3">Shown in the sidebar and on every exported PDF / Excel report. PNG, JPEG, WebP or SVG up to 2 MB.</p>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                  {tenantInfo.has_logo ? (
                    <img
                      src={`${API}/tenant/logo?v=${tenantInfo.settings?.logo_uploaded_at || ''}${viewingOrg?.id ? `&view_tenant_id=${viewingOrg.id}` : ''}`}
                      alt="Tenant logo"
                      className="max-w-full max-h-full object-contain"
                      data-testid="tenant-logo-preview"
                    />
                  ) : (
                    <Building2 className="w-8 h-8 text-slate-300" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="tenant-logo-upload"
                    className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-slate-700 w-fit"
                    data-testid="tenant-logo-upload-label"
                  >
                    {tenantInfo.has_logo ? 'Replace logo' : 'Upload logo'}
                    <input
                      id="tenant-logo-upload"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden"
                      data-testid="tenant-logo-upload-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const fd = new FormData();
                        fd.append('file', file);
                        try {
                          const q = viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
                          await axios.post(`${API}/tenant/logo${q}`, fd, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          toast.success('Logo uploaded');
                          e.target.value = '';
                          fetchData();
                          if (typeof refreshEnabledFrameworks === 'function') refreshEnabledFrameworks();
                          window.dispatchEvent(new Event('tenant-logo-updated'));
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Upload failed');
                        }
                      }}
                    />
                  </label>
                  {tenantInfo.has_logo && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm('Remove tenant logo?')) return;
                        try {
                          const q = viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
                          await axios.delete(`${API}/tenant/logo${q}`);
                          toast.success('Logo removed');
                          fetchData();
                          window.dispatchEvent(new Event('tenant-logo-updated'));
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Remove failed');
                        }
                      }}
                      className="text-xs text-red-600 hover:text-red-700 w-fit"
                      data-testid="tenant-logo-remove-btn"
                    >
                      Remove logo
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Personal MFA Settings Section */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-slate-600" />
            <CardTitle className="text-lg">My Security Settings</CardTitle>
          </div>
          <CardDescription>
            Manage your personal two-factor authentication settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MFASettings />
        </CardContent>
      </Card>

      {/* Organization MFA Policy Section (Admin only) */}
      {isAdmin && (
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-slate-600" />
              <CardTitle className="text-lg">Organization MFA Policy</CardTitle>
            </div>
            <CardDescription>
              Configure multi-factor authentication requirements for your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingMfaPolicy ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : mfaPolicy ? (
              <div className="space-y-6">
                {/* MFA Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-600">Total Users</p>
                    <p className="text-2xl font-semibold text-slate-900">{mfaPolicy.stats?.total_users || 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-600">MFA Enabled</p>
                    <p className="text-2xl font-semibold text-green-600">{mfaPolicy.stats?.mfa_enabled_users || 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-600">Adoption Rate</p>
                    <p className="text-2xl font-semibold text-slate-900">{mfaPolicy.stats?.mfa_adoption_rate || 0}%</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-600">Admin MFA</p>
                    <p className="text-2xl font-semibold text-slate-900">
                      {mfaPolicy.stats?.admin_mfa_enabled || 0}/{mfaPolicy.stats?.admin_users || 0}
                    </p>
                  </div>
                </div>

                {/* Policy Toggles */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                    <div>
                      <p className="font-medium text-slate-900">Require MFA for Administrators</p>
                      <p className="text-sm text-slate-600">
                        Admins must enable MFA before accessing administrative features
                      </p>
                    </div>
                    <Switch
                      checked={mfaPolicy.require_mfa_for_admins || false}
                      onCheckedChange={(checked) => handleMfaPolicyChange('require_mfa_for_admins', checked)}
                      disabled={savingMfaPolicy}
                      data-testid="mfa-require-admins-switch"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                    <div>
                      <p className="font-medium text-slate-900">Require MFA for All Users</p>
                      <p className="text-sm text-slate-600">
                        All users must enable MFA before accessing the platform
                      </p>
                    </div>
                    <Switch
                      checked={mfaPolicy.require_mfa_for_all_users || false}
                      onCheckedChange={(checked) => handleMfaPolicyChange('require_mfa_for_all_users', checked)}
                      disabled={savingMfaPolicy}
                      data-testid="mfa-require-all-switch"
                    />
                  </div>
                </div>

                {/* Warning */}
                {(mfaPolicy.require_mfa_for_admins || mfaPolicy.require_mfa_for_all_users) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">MFA Enforcement Enabled</p>
                      <p className="text-amber-700">
                        Users who haven't set up MFA will be prompted to do so upon their next login.
                        They won't be able to access the platform until MFA is configured.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">Unable to load MFA policy</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Compliance Frameworks Section */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-slate-600" />
              <CardTitle className="text-lg">Compliance Frameworks</CardTitle>
            </div>
            <span className="text-sm text-slate-500">
              {frameworks.filter(f => f.enabled).length} of {frameworks.length} enabled
            </span>
          </div>
          <CardDescription>
            Choose which compliance frameworks your organization needs to track. Disabled frameworks will be hidden from navigation and reports.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingFrameworks ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {frameworks.map((framework) => (
                <div 
                  key={framework.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                    framework.enabled 
                      ? 'bg-slate-50 border-slate-200' 
                      : 'bg-white border-slate-100'
                  }`}
                  data-testid={`framework-card-${framework.id}`}
                >
                  <div className="flex-1 mr-4">
                    <div className="flex items-center gap-2">
                      <Shield className={`w-4 h-4 ${framework.enabled ? 'text-slate-700' : 'text-slate-400'}`} />
                      <span className={`font-medium ${framework.enabled ? 'text-slate-900' : 'text-slate-500'}`}>
                        {framework.name}
                      </span>
                    </div>
                    <p className={`text-xs mt-1 ${framework.enabled ? 'text-slate-600' : 'text-slate-400'}`}>
                      {framework.description}
                    </p>
                  </div>
                  <Switch
                    checked={framework.enabled}
                    onCheckedChange={() => handleToggleFramework(framework.id, framework.enabled)}
                    data-testid={`framework-switch-${framework.id}`}
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Team Members Section */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-600" />
            Team Members ({users.length})
          </h2>
          <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite New Team Member</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateInvite} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <select
                    id="role"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="client">Client (View & Comment)</option>
                    <option value="admin">Admin (Full Access)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {inviteRole === 'admin' 
                      ? 'Admins can modify control status, invite users, and manage settings.'
                      : 'Clients can view controls and add comments but cannot change status.'}
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="submit" disabled={creatingInvite} className="flex-1">
                    {creatingInvite ? 'Creating...' : 'Create Invite'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowInviteDialog(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Joined</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {users.map((member) => (
                <tr key={member.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="text-sm font-medium text-slate-600">
                          {member.name?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {member.name}
                          {member.id === user?.id && (
                            <span className="ml-2 text-xs text-gray-400">(You)</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">{member.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(member.role)}`}>
                      {member.role === 'admin' ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                      {member.role === 'admin' ? 'Admin' : 'Client'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(member.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    {member.id !== user?.id ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openRemoveDialog(member)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                      >
                        <UserMinus className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Invites Section */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-slate-600" />
          Pending Invitations ({invites.filter(i => i.status === 'pending').length})
        </h2>

        {invites.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No pending invitations</p>
            <p className="text-sm">Invite team members to collaborate</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Invite Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Expires</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {invites.map((invite) => (
                  <tr key={invite.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className="text-sm">{invite.email}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(invite.role)}`}>
                        {invite.role === 'admin' ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                        {invite.role === 'admin' ? 'Admin' : 'Client'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                          {invite.invite_code}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyInviteCode(invite.invite_code)}
                          className="h-7 w-7 p-0"
                        >
                          {copiedCode === invite.invite_code ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(invite.status)}`}>
                        {invite.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(invite.expires_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleRegenerateInvite(invite)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                          title="Generate new code"
                          data-testid={`regenerate-invite-${invite.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteInvite(invite.id)}
                          className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                          title="Delete invite"
                          data-testid={`delete-invite-${invite.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Role Permissions Info */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Role Permissions</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white p-4 rounded-lg border border-purple-200">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-purple-600" />
              <h3 className="font-medium text-purple-800">Admin</h3>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✓ View all controls and frameworks</li>
              <li>✓ Update control compliance status</li>
              <li>✓ Add evidence and comments</li>
              <li>✓ Invite and manage team members</li>
              <li>✓ Configure integrations</li>
              <li>✓ Access organization settings</li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-blue-800">Client</h3>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✓ View all controls and frameworks</li>
              <li>✗ Cannot update compliance status</li>
              <li>✓ Add evidence and comments</li>
              <li>✗ Cannot invite team members</li>
              <li>✗ Cannot configure integrations</li>
              <li>✗ Cannot access organization settings</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Super Admin: Danger Zone */}
      {isSuperAdmin && (
        <Card className="border-red-200 bg-red-50/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone (Super Admin)
            </CardTitle>
            <CardDescription className="text-red-700/80">
              Destructive actions. These cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResetAcknowledgementSection viewingOrg={viewingOrg} />
          </CardContent>
        </Card>
      )}

      {/* Invite Success Dialog */}
      <Dialog open={showInviteSuccessDialog} onOpenChange={setShowInviteSuccessDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              Invite Created Successfully!
            </DialogTitle>
          </DialogHeader>
          
          {newInviteData && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 mb-2">
                  Invite created for <strong>{newInviteData.email}</strong>
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-green-700">Invite Code:</span>
                  <code className="bg-white px-3 py-1 rounded border border-green-300 font-mono text-lg font-bold">
                    {newInviteData.invite_code}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyInviteCode(newInviteData.invite_code)}
                    className="h-8 w-8 p-0"
                  >
                    {copiedCode === newInviteData.invite_code ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Share these instructions with {newInviteData.email}:
                </h4>
                <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                  <li>Go to <strong>{window.location.origin}</strong></li>
                  <li>Click <strong>&quot;Have an invite code? Join existing org&quot;</strong></li>
                  <li>Enter invite code: <code className="bg-white px-1 rounded">{newInviteData.invite_code}</code></li>
                  <li>Create account with email: <strong>{newInviteData.email}</strong></li>
                  <li>Set a password and sign up</li>
                </ol>
                <p className="text-xs text-slate-500 mt-3">
                  Role: {newInviteData.role === 'admin' ? 'Admin (Full Access)' : 'Client (View & Comment)'} • Expires in 7 days
                </p>
              </div>

              <div className="flex gap-2">
                <Button onClick={copyInviteInstructions} className="flex-1">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Instructions
                </Button>
                <Button variant="outline" onClick={() => setShowInviteSuccessDialog(false)}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove User Confirmation Dialog */}
      <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <UserMinus className="w-5 h-5" />
              Remove User from Organization
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {userToRemove && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  Are you sure you want to remove <strong>{userToRemove.name}</strong> ({userToRemove.email}) from your organization?
                </p>
                <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                  <li><strong>Their account will be permanently deleted</strong></li>
                  <li>They will not be able to login anymore</li>
                  <li>Their work and comments will be preserved</li>
                  <li>They will need a new invite to rejoin</li>
                </ul>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  onClick={handleRemoveUser}
                  disabled={removingUser}
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {removingUser ? 'Deleting...' : 'Delete User'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRemoveDialog(false);
                    setUserToRemove(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResetAcknowledgementSection({ viewingOrg }) {
  const [scope, setScope] = useState('tenant'); // 'tenant' or 'all'
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const targetLabel = scope === 'all' ? 'ALL TENANTS' : (viewingOrg?.name || 'this tenant');
  const expectedConfirm = scope === 'all' ? 'RESET ALL' : 'RESET';

  const handleReset = async () => {
    if (confirmText.trim() !== expectedConfirm) {
      toast.error(`Type "${expectedConfirm}" exactly to confirm`);
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const body = { confirm: true, scope };
      if (scope === 'tenant') body.tenant_id = viewingOrg?.id;
      const res = await axios.post(`${API}/admin/reset-acknowledgement-data`, body);
      setResult(res.data);
      setConfirmText('');
      toast.success(`Acknowledgement data reset (${res.data.acks_deleted} acks, ${res.data.users_deleted} users)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-semibold text-slate-900">Reset Policy Acknowledgement Data</p>
        <p className="text-xs text-slate-600 mt-1">
          Wipes ALL acknowledgement records, bundles, and imported external users. Existing policies remain intact,
          but their assignment links will be invalidated and users will need to be re-imported and re-assigned.
        </p>
      </div>

      <div>
        <Label className="text-xs">Scope</Label>
        <div className="flex flex-col gap-2 mt-1">
          <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-slate-200 hover:bg-white">
            <input
              type="radio"
              checked={scope === 'tenant'}
              onChange={() => { setScope('tenant'); setConfirmText(''); }}
              className="mt-1"
              disabled={!viewingOrg}
              data-testid="reset-scope-tenant"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-900">This organization only</p>
              <p className="text-xs text-slate-500">{viewingOrg?.name || 'Select an organization first via the org switcher'}</p>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-red-300 hover:bg-white bg-red-50">
            <input
              type="radio"
              checked={scope === 'all'}
              onChange={() => { setScope('all'); setConfirmText(''); }}
              className="mt-1"
              data-testid="reset-scope-all"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">All tenants (global)</p>
              <p className="text-xs text-red-700">Wipes acknowledgement data for every organization on the platform.</p>
            </div>
          </label>
        </div>
      </div>

      <div>
        <Label className="text-xs">
          Type <code className="font-bold text-red-700">{expectedConfirm}</code> to confirm
        </Label>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={expectedConfirm}
          className="mt-1"
          data-testid="reset-confirm-input"
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="destructive"
          onClick={handleReset}
          disabled={busy || confirmText.trim() !== expectedConfirm || (scope === 'tenant' && !viewingOrg)}
          data-testid="reset-ack-data-btn"
        >
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting...</> : <><Trash2 className="w-4 h-4 mr-2" />Reset Acknowledgement Data ({targetLabel})</>}
        </Button>
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
          <p className="font-semibold mb-1">Reset complete</p>
          <ul className="space-y-0.5">
            <li>• Acknowledgements deleted: <strong>{result.acks_deleted}</strong></li>
            <li>• Bundles deleted: <strong>{result.bundles_deleted}</strong></li>
            <li>• External users deleted: <strong>{result.users_deleted}</strong></li>
            <li>• Policy ack tokens cleared: <strong>{result.ack_tokens_cleared}</strong></li>
          </ul>
        </div>
      )}
    </div>
  );
}
